import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";

export interface StorageUpgradeOption {
  label: string;
  price: number;
  currencyCode: string;
  periodLabel: string;
  qty: number;
  soldOut: boolean;
}

// regions that cannot hand out additional storage right now; subscribers there
// keep what they already have but cannot pick a bigger upgrade. Empty while
// every region has storage to spare (Amsterdam was listed here until 2026-10).
const storageUpgradeSoldOutRegions: string[] = [];

const storageUpgradePrices = {
  "1m": {
    _: {
      price: 4.0,
      currencyCode: "EUR",
    },
    US: {
      price: 4.0,
      currencyCode: "USD",
    },
    GB: {
      price: 3.5,
      currencyCode: "GBP",
    },
    CA: {
      price: 5.5,
      currencyCode: "CAD",
    },
  },
  "6m": {
    _: {
      price: 3.0,
      currencyCode: "EUR",
    },
    US: {
      price: 3.0,
      currencyCode: "USD",
    },
    GB: {
      price: 2.5,
      currencyCode: "GBP",
    },
    CA: {
      price: 4.0,
      currencyCode: "CAD",
    },
  },
};

@Component({
  selector: "app-select-storage-upgrade",
  templateUrl: "./select-storage-upgrade.component.html",
  styleUrl: "./select-storage-upgrade.component.scss",
})
export class SelectStorageUpgradeComponent implements OnChanges {
  @Input() countryCode: string;
  @Input() currentQuantity: number;
  @Input() region: string;
  @Input() planDuration: string;
  // percent-off applied by the Stripe coupon at checkout; the session-level
  // coupon discounts storage line items too, so advertise the same price
  @Input() discountPercent = 0;
  @Output() storageUpgradeChange = new EventEmitter<StorageUpgradeOption>();

  public storageUpgradeOptions: StorageUpgradeOption[] = [
    {
      label: "None",
      price: 0,
      currencyCode: "EUR",
      periodLabel: "/mo",
      qty: 0,
      soldOut: false,
    },
    {
      label: "+250 GB",
      price: 4,
      currencyCode: "EUR",
      periodLabel: "/mo",
      qty: 1,
      soldOut: false,
    },
    {
      label: "+500 GB",
      price: 8,
      currencyCode: "EUR",
      periodLabel: "/mo",
      qty: 2,
      soldOut: false,
    },
    {
      label: "+750 GB",
      price: 12,
      currencyCode: "EUR",
      periodLabel: "/mo",
      qty: 3,
      soldOut: false,
    },
  ];
  public storageUpgrade = this.storageUpgradeOptions[0];
  public storageUpgradeDropdownCollapsed = true;

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    this.updateStorageOptions(
      changes.countryCode?.currentValue || this.countryCode || "",
    );
    // only a changed current quantity resets the selection; repricing or a
    // region change keeps whatever the user picked
    const qty = changes.currentQuantity
      ? changes.currentQuantity.currentValue || 0
      : this.storageUpgrade.qty;
    this.storageUpgrade = this.storageUpgradeOptions[qty];
    // the picked size may have just gone out of stock, e.g. after switching to
    // a sold-out region; fall back to what the subscriber already has
    if (this.storageUpgrade.soldOut) {
      this.storageUpgrade =
        this.storageUpgradeOptions[this.currentQuantity || 0];
    }
    this.emitStorageUpgradeChange(this.storageUpgrade);
  }

  public selectStorageUpgrade(option: StorageUpgradeOption) {
    if (option.soldOut) {
      return;
    }
    this.storageUpgrade = option;
    this.storageUpgradeDropdownCollapsed = true;
    this.emitStorageUpgradeChange(option);
  }

  public emitStorageUpgradeChange(option: StorageUpgradeOption) {
    this.storageUpgradeChange.emit(option);
  }

  private updateStorageOptions(countryCode: string) {
    const priceInfo =
      storageUpgradePrices[this.planDuration][countryCode] ||
      storageUpgradePrices[this.planDuration]["_"];

    let options: StorageUpgradeOption[] = [];
    const values = [0, 250, 500, 750];
    const regionSoldOut = storageUpgradeSoldOutRegions.includes(this.region);
    const periodLabel = this.planDuration === "6m" ? "/6m" : "/mo";
    const periodMultiplier = this.planDuration === "6m" ? 6 : 1;

    values.forEach((v, i) => {
      // mirrors Stripe's math: discount rounded to whole cents before subtracting
      const cents = Math.round(i * priceInfo.price * periodMultiplier * 100);
      const discountCents = Math.round((cents * this.discountPercent) / 100);
      options.push({
        label: v !== 0 ? `+${v} GB` : "None",
        price: (cents - discountCents) / 100,
        currencyCode: priceInfo.currencyCode,
        periodLabel: periodLabel,
        qty: i,
        // a sold-out region can still serve the storage a subscriber already
        // pays for, so only bigger upgrades are blocked
        soldOut: regionSoldOut && i > (this.currentQuantity || 0),
      });
    });

    this.storageUpgradeOptions = options;
  }
}
